import { urls } from '@app/services/api/urls';

// The Redpen bug and feature-request widget is switched on in Django admin
// (Site widget settings), not at build time, so a deploy ships with it off and
// an admin turns it on later without a rebuild (ZLAYT-1542).
//
// VITE_REDPEN_WIDGET_ID is a local dev only override: on the Vite dev server
// (import.meta.env.DEV) it wins and the backend is not asked. Built bundles
// ignore it.
//
// Plain fetch, not the shared axios instance: axios would record a failed
// call as the "last failed request" shown in the next error toast, and would
// send a 401 to the sign-in page. A widget that fails to load must stay
// silent (ZLAYT-1421).

const REDPEN_SCRIPT_SRC = 'https://app.redpen.ai/redpenWebWidget.js';
const FETCH_TIMEOUT_MS = 10_000;

type PublicWidgets = {
  redpen?: { enabled?: unknown; widget_id?: unknown };
};

export function injectRedpenWidget(widgetId: string): void {
  if (!widgetId || document.getElementById('redpen-widget-script')) {
    return;
  }
  const cfg = document.createElement('script');
  cfg.id = 'redpen-widget-data';
  cfg.type = 'text/javascript';
  // JSON.stringify so an id from the backend can never break out of the string.
  cfg.text = `window.redpenWidgetConfig = { widgetId: ${JSON.stringify(widgetId)} };`;
  document.body.appendChild(cfg);

  const s = document.createElement('script');
  s.id = 'redpen-widget-script';
  s.defer = true;
  s.async = true;
  s.src = REDPEN_SCRIPT_SRC;
  document.body.appendChild(s);
}

export async function fetchRedpenWidgetId(
  apiBase: string | undefined = import.meta.env.VITE_APP_API_URL
): Promise<string | null> {
  if (!apiBase) {
    return null;
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`${apiBase}${urls.publicWidgets}`, {
      method: 'GET',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) {
      return null;
    }
    const body = (await response.json()) as PublicWidgets;
    const redpen = body?.redpen;
    if (
      redpen?.enabled === true &&
      typeof redpen.widget_id === 'string' &&
      redpen.widget_id.trim()
    ) {
      return redpen.widget_id.trim();
    }
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function loadRedpenWidget(
  override: string | undefined = import.meta.env.VITE_REDPEN_WIDGET_ID,
  isDevServer: boolean = import.meta.env.DEV
): Promise<void> {
  // The override counts only on the local dev server. Every built bundle
  // (dev, staging, production) asks the backend, because CI copies a
  // variables file into .env and a stray id there must not switch it on.
  const localId = isDevServer ? override?.trim() : '';
  const widgetId = localId || (await fetchRedpenWidgetId());
  if (widgetId) {
    injectRedpenWidget(widgetId);
  }
}
